import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "./style.css";
import acompanhamentoApi from "../../service/modules/acompanhamento.api";
import freightService from "../../service/freight.service";
import useRecargaPeriodica from "../../util/freight/useRecargaPeriodica";
import { translatorForTheStatusOfStoppingPoints } from "../../view/frete-copy/action/action";
import { costurar } from "./costura";
import { posicaoDoCaminhao } from "./posicao-do-caminhao";
import {
  AZUL_DO_TRAJETO,
  CINZA_PERCURSO,
  COR_DA_COLETA,
  COR_DA_ENTREGA,
  COR_DA_LACUNA,
  VIOLETA_DO_CANHOTO,
  corDoStatus,
} from "./cores";

/** A mesma base das outras telas de mapa, para o painel não parecer dois sistemas. */
const TILE_CLARO = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

/**
 * Teto de páginas do trajeto, que carrega do mais novo para o mais antigo. Com os pontos de movimento
 * do app novo uma viagem soma de 1.300 a 2.400 capturas por dia, e 40 páginas de mil cobrem os 15 dias
 * com folga. O mapa compacto da lista fica nas duas mais novas: clicar de frete em frete não pode puxar
 * a viagem inteira de cada um. Acima do teto a tela avisa que faltou o trecho mais antigo.
 */
const MAX_PAGINAS = 40;
const MAX_PAGINAS_COMPACTO = 2;

/**
 * De quanto em quanto tempo a atualização leva junto a contagem do que o mapa tem. É ela que pega o
 * ponto que chegou fora de ordem, atrás do cursor; entre uma conferência e outra, só a sonda.
 */
const CONFERENCIA_MS = 10 * 60 * 1000;

/** Depois de falha, a atualização espera 30 s, depois o dobro, até 5 min. */
const PRIMEIRA_ESPERA_MS = 30 * 1000;
const ESPERA_MAXIMA_MS = 5 * 60 * 1000;

/**
 * Nível de rua, e não mais: o canhoto conferido fica a poucos metros do endereço, e enquadrar os dois
 * levaria o mapa ao zoom máximo, sem rua em volta para reconhecer o lugar.
 */
const ZOOM_DO_CANHOTO = 17;

/** Acima disso a nuvem de bolinhas cinza vira borrão e a polyline sozinha lê melhor. */
const TETO_DE_PONTOS_DESENHADOS = 400;

/**
 * De onde o mapa lê. O painel usa as rotas logadas e escuta a última posição no Firestore; a página
 * pública do link passa as rotas por token e nenhuma escuta, porque quem abre o link não tem login.
 * Constante do módulo, e não objeto montado na renderização: os efeitos dependem dela.
 */
const FONTES_DO_PAINEL = {
  buscarTrajeto: (id, params) => acompanhamentoApi.buscarTrajeto(id, params),
  novidadesDoTrajeto: (id, params) =>
    acompanhamentoApi.novidadesDoTrajeto(id, params),
  escutarUltimaPosicao: (id, aoChegar, aoFalhar) =>
    freightService.subscribeUltimaPosicao(id, aoChegar, aoFalhar),
};

function coordenadaValida(par) {
  return (
    Array.isArray(par) &&
    Number.isFinite(Number(par[0])) &&
    Number.isFinite(Number(par[1])) &&
    Number(par[0]) !== 0 &&
    Number(par[1]) !== 0
  );
}

function numero(valor) {
  const n = Number(valor);
  return Number.isFinite(n) && n !== 0 ? n : null;
}

function paraMillis(valor) {
  if (valor == null) return null;
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : null;
  if (typeof valor.toMillis === "function") return valor.toMillis();
  if (typeof valor.seconds === "number") {
    return valor.seconds * 1000 + Math.floor((valor.nanoseconds || 0) / 1e6);
  }
  const d = new Date(valor).getTime();
  return Number.isNaN(d) ? null : d;
}

function duracao(minutos) {
  if (!minutos) return "menos de 1min";
  if (minutos < 60) return `${minutos}min`;
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  return resto ? `${horas}h${String(resto).padStart(2, "0")}` : `${horas}h`;
}

function dataHora(ms) {
  return ms ? new Date(ms).toLocaleString("pt-BR") : "—";
}

function rotuloDoStatus(status) {
  const traduzido = translatorForTheStatusOfStoppingPoints(status);
  return Array.isArray(traduzido) ? String(status || "Etapa") : traduzido;
}

/** A página no formato do mapa ao vivo, ou no de antes, quando o backend ainda não o tem. */
function parteDaPagina(pagina) {
  return (
    pagina.finalizado || {
      trechos: pagina.trechos || [],
      paradas: pagina.paradas || [],
      lacunas: pagina.lacunas || [],
      operacionais: pagina.operacionais || [],
      primeiroMs: pagina.resumo?.primeiroMs ?? null,
      ultimoMs: pagina.resumo?.ultimoMs ?? null,
    }
  );
}

function resumoDe(estado) {
  return {
    ...costurar([...estado.partes, estado.cauda]),
    formato: estado.formato,
    legendaDisponivel: estado.legendaDisponivel,
    faltou: estado.faltou,
  };
}

/** O que a conferência compara com o banco: os documentos de todas as partes que o mapa tem. */
function documentosDe(estado) {
  return [...estado.partes, estado.cauda].reduce(
    (soma, parte) => soma + (parte?.documentos || 0),
    0,
  );
}

function pinoDeGota(cor, titulo) {
  return L.divIcon({
    className: "mapa-rota__pino",
    html: `<span title="${titulo}" style="background:${cor}"></span>`,
    iconSize: [22, 22],
    iconAnchor: [11, 22],
  });
}

const GLIFO_DO_CANHOTO =
  "M6 2.5h12v19l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5-2 1.5zM9 7.5h6V10H9zm0 5h4V15H9z";

/**
 * Redondo e centrado, como o que o mapa desenha a partir de captura — gota é lugar planejado. E com o
 * centro no ponto, o pino fica no meio do círculo da precisão.
 */
function pinoDoCanhoto() {
  return L.divIcon({
    className: "mapa-rota__canhoto",
    html: `<span class="mapa-rota__canhoto-corpo" style="background:${VIOLETA_DO_CANHOTO}">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path fill-rule="evenodd" d="${GLIFO_DO_CANHOTO}" />
        </svg>
      </span>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
    tooltipAnchor: [14, 0],
  });
}

/** O nome da parada vem do cadastro, e o Leaflet trata texto de dica como HTML. Nó a nó, é só texto. */
function dicaDoCanhoto(canhoto) {
  const dica = document.createElement("div");
  const titulo = document.createElement("strong");
  titulo.textContent = canhoto.titulo;
  dica.appendChild(titulo);
  for (const linha of canhoto.linhas || []) {
    const texto = document.createElement("div");
    texto.textContent = linha;
    dica.appendChild(texto);
  }
  return dica;
}

function pontosDosCanhotos(canhotos) {
  return (canhotos || []).flatMap((canhoto) =>
    [[canhoto.latitude, canhoto.longitude], canhoto.referencia].filter(
      coordenadaValida,
    ),
  );
}

/**
 * O motorista não é gota como coleta e entrega: é redondo, maior, escuro e leva o caminhão dentro.
 *
 * Os três eram o mesmo pino de 22 px mudando só a cor, e o do motorista era um vermelho vizinho do
 * vermelho da entrega — de longe, dois pinos iguais. Quem olha o mapa precisa achar o caminhão em
 * um relance, não comparar tons.
 */
function pinoDoMotorista(titulo) {
  return L.divIcon({
    className: "mapa-rota__motorista",
    html: `<span class="mapa-rota__motorista-pulso"></span>
      <span class="mapa-rota__motorista-corpo" title="${titulo}">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M2 6.5h11.2v8.2H2z" />
          <path d="M13.2 9.4h3.6l3.2 3.1v2.2h-6.8z" />
          <circle cx="6.6" cy="17" r="2.1" />
          <circle cx="16.9" cy="17" r="2.1" />
        </svg>
      </span>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });
}

function MapaRota({
  frete,
  compacto = false,
  fontes = FONTES_DO_PAINEL,
  maxPaginas,
  canhotos,
  foco,
}) {
  const containerRef = useRef(null);
  const mapaRef = useRef(null);
  const trilhaRef = useRef(null);
  const motoristaRef = useRef(null);
  const canhotosRef = useRef(null);
  const marcadoresDosCanhotosRef = useRef(new Map());
  const canhotosEnquadradosRef = useRef(null);
  const enquadradoRef = useRef(null);
  // O que a atualização carrega de um tique para o outro. Fica em ref porque muda a cada consulta e não
  // redesenha nada sozinho: quem redesenha é o `resumo`. A geração descarta resposta de um frete que já
  // saiu da tela, ou de uma carga que outra substituiu.
  const estadoRef = useRef(null);
  const geracaoRef = useRef(0);
  const [resumo, setResumo] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const [aoVivo, setAoVivo] = useState(null);
  const [intervalo, setIntervalo] = useState(0);

  const freteId = frete?.id;
  const teto = maxPaginas || (compacto ? MAX_PAGINAS_COMPACTO : MAX_PAGINAS);

  // O desenho depende destes valores, e não do objeto `frete`: a lista e o detalhe trocam o objeto a
  // cada recarga, e redesenhar a viagem inteira por isso fecharia a dica que o operador estivesse lendo.
  const coletaLat = numero(frete?.firstDelivery?.coordinates?.latitude);
  const coletaLng = numero(frete?.firstDelivery?.coordinates?.longitude);
  const entregaLat = numero(frete?.lastDelivery?.coordinates?.latitude);
  const entregaLng = numero(frete?.lastDelivery?.coordinates?.longitude);
  const motorista = aoVivo || frete?.lastPosition;
  const motoristaLat = numero(motorista?.latitude);
  const motoristaLng = numero(motorista?.longitude);
  const motoristaMs = paraMillis(motorista?.timestamp ?? motorista?.capturedAt);

  // Pelo mesmo motivo os canhotos entram por uma chave de texto: o detalhe refaz a lista a cada escrita
  // em qualquer ponto de parada, e só mudança no que se desenha pode redesenhar.
  const chaveDosCanhotos = JSON.stringify(canhotos ?? null);
  const listaDeCanhotos = useMemo(
    () => JSON.parse(chaveDosCanhotos),
    [chaveDosCanhotos],
  );
  const focoId = foco?.id;
  const focoVez = foco?.vez;

  useEffect(() => {
    const el = containerRef.current;
    if (!el || mapaRef.current) return undefined;

    const mapa = L.map(el, {
      zoomControl: true,
      attributionControl: true,
    }).setView([-15.78, -47.93], 4);
    L.tileLayer(TILE_CLARO, {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(mapa);
    mapaRef.current = mapa;

    // O container tem altura elástica no painel de tempo real: ele acompanha a tabela ao lado, que
    // muda de altura quando o operador troca o tamanho da página. O Leaflet calcula o tamanho uma
    // vez na criação e não observa o DOM — sem isto o mapa fica com faixa cinza onde cresceu.
    const observador = new ResizeObserver(() => mapa.invalidateSize());
    observador.observe(el);

    return () => {
      observador.disconnect();
      mapa.remove();
      mapaRef.current = null;
    };
  }, []);

  /**
   * Carga do zero, do mais novo para o mais antigo: a primeira página já traz o trecho recente e a
   * cauda, e as mais antigas entram na frente conforme chegam. Em segundo plano — quando a atualização
   * manda recarregar — a tela continua com a viagem anterior e só troca no fim, de uma vez.
   */
  const carregarDoZero = useCallback(
    async ({ emSegundoPlano = false } = {}) => {
      if (!freteId) return;
      const geracao = ++geracaoRef.current;
      const vale = () => geracaoRef.current === geracao;
      const anterior = estadoRef.current;
      const novo = {
        geracao,
        partes: [],
        cauda: null,
        cursorDaCauda: null,
        cursorFinal: null,
        desde: undefined,
        aoVivo: false,
        intervaloMs: 0,
        carregando: true,
        conferidoEm: Date.now(),
        falhas: 0,
        esperarAte: 0,
        formato: undefined,
        legendaDisponivel: undefined,
        faltou: null,
      };

      if (emSegundoPlano && anterior) {
        // Os tiques param enquanto a viagem é refeita por trás.
        anterior.carregando = true;
      } else {
        estadoRef.current = novo;
        setResumo(null);
        setErro(null);
        setCarregando(true);
      }

      const publicar = () => {
        if (vale() && estadoRef.current === novo) setResumo(resumoDe(novo));
      };

      try {
        let ordem = "desc";
        let resposta;
        try {
          resposta = await fontes.buscarTrajeto(freteId, { ordem });
        } catch (e) {
          if (e?.response?.status !== 400) throw e;
          // Backend de antes do mapa ao vivo: o DTO recusa `ordem`. Carrega como antes, sem atualizar.
          ordem = undefined;
          resposta = await fontes.buscarTrajeto(freteId);
        }
        if (!vale()) return;

        let pagina = resposta?.data?.data || {};
        novo.formato = pagina.resumo?.formato;
        novo.legendaDisponivel = pagina.resumo?.legendaDisponivel;
        novo.partes.push(parteDaPagina(pagina));
        if (ordem && pagina.finalizado) {
          novo.aoVivo = true;
          novo.cauda = pagina.cauda || null;
          novo.cursorDaCauda = pagina.resumo?.cursorDaCauda ?? null;
          novo.cursorFinal = pagina.resumo?.cursorFinal ?? null;
          novo.intervaloMs = pagina.resumo?.intervaloAoVivoMs ?? 0;
        }
        publicar();

        let cursor = pagina.resumo?.proximoCursor;
        let paginas = 1;
        while (cursor && paginas < teto) {
          // eslint-disable-next-line no-await-in-loop
          resposta = await fontes.buscarTrajeto(freteId, {
            cursor,
            ordem,
          });
          if (!vale()) return;

          pagina = resposta?.data?.data || {};
          // Do mais novo para o mais antigo, cada página entra na frente; no formato de antes, atrás.
          if (ordem) novo.partes.unshift(parteDaPagina(pagina));
          else novo.partes.push(parteDaPagina(pagina));
          paginas += 1;
          cursor = pagina.resumo?.proximoCursor;
          publicar();
        }

        // Parou no teto: o que falta é o começo da viagem, e a conferência passa a contar só a
        // partir do documento mais antigo que o mapa tem.
        novo.faltou = cursor ? (ordem ? "antigo" : "recente") : null;
        novo.desde = cursor || undefined;
        novo.carregando = false;
        novo.conferidoEm = Date.now();

        estadoRef.current = novo;
        setResumo(resumoDe(novo));
        setIntervalo(novo.aoVivo ? novo.intervaloMs : 0);
      } catch (e) {
        if (!vale()) return;
        console.error("Erro ao carregar o trajeto do frete:", e);
        if (emSegundoPlano && anterior) {
          // Recarga por trás que falhou deixa a viagem anterior na tela, e os tiques voltam.
          anterior.carregando = false;
          return;
        }
        setErro("Não foi possível carregar o trajeto deste frete.");
      } finally {
        if (vale() && !emSegundoPlano) setCarregando(false);
      }
    },
    [freteId, teto, fontes],
  );

  useEffect(() => {
    setIntervalo(0);
    if (!freteId) {
      geracaoRef.current += 1;
      estadoRef.current = null;
      setResumo(null);
      setErro(null);
      return undefined;
    }

    carregarDoZero();
    return () => {
      geracaoRef.current += 1;
    };
  }, [freteId, carregarDoZero]);

  /**
   * Um tique do mapa ao vivo. Sem ponto novo, a sonda responde e nada muda; com ponto novo, a cauda
   * volta relida e é trocada inteira, e o que ficou final soma às partes estáveis.
   */
  const atualizar = useCallback(async () => {
    const estado = estadoRef.current;
    if (!freteId || !estado || !estado.aoVivo || estado.carregando) return;
    if (Date.now() < estado.esperarAte) return;

    if (!estado.cursorFinal) {
      // Viagem ainda sem posição: pede a primeira página de novo até a primeira aparecer.
      await carregarDoZero({ emSegundoPlano: true });
      return;
    }

    const conferir = Date.now() - estado.conferidoEm >= CONFERENCIA_MS;
    const params = {
      aPartirDe: estado.cursorDaCauda,
      depoisDe: estado.cursorFinal,
    };
    if (conferir) {
      params.total = documentosDe(estado);
      if (estado.desde) params.desde = estado.desde;
    }

    let novidades;
    try {
      const resposta = await fontes.novidadesDoTrajeto(freteId, params);
      novidades = resposta?.data?.data || {};
    } catch (e) {
      if (estadoRef.current !== estado) return;
      const status = e?.response?.status;
      if (status === 400 || status === 404 || status === 410) {
        // Backend sem a rota nova, frete que saiu do escopo, ou link público que expirou ou foi
        // desativado com a página aberta: esperar e tentar de novo só repetiria a recusa.
        estado.aoVivo = false;
        setIntervalo(0);
      } else {
        estado.falhas += 1;
        estado.esperarAte =
          Date.now() +
          Math.min(ESPERA_MAXIMA_MS, PRIMEIRA_ESPERA_MS * 2 ** (estado.falhas - 1));
      }
      console.error("Erro ao atualizar o trajeto do frete:", e);
      return;
    }
    if (estadoRef.current !== estado) return;

    estado.falhas = 0;
    estado.esperarAte = 0;
    if (conferir) estado.conferidoEm = Date.now();
    if (Number.isFinite(novidades.intervaloAoVivoMs)) {
      setIntervalo(novidades.intervaloAoVivoMs);
    }

    if (novidades.recarregar) {
      await carregarDoZero({ emSegundoPlano: true });
      return;
    }
    if (novidades.semNovidade) return;

    estado.partes.push(novidades.finalizado);
    estado.cauda = novidades.cauda;
    estado.cursorDaCauda = novidades.cursorDaCauda;
    estado.cursorFinal = novidades.cursorFinal;
    setResumo(resumoDe(estado));
  }, [freteId, carregarDoZero, fontes]);

  useRecargaPeriodica(atualizar, intervalo);

  // Sem escuta — a página pública — o caminhão fica na `lastPosition` que vem com o frete.
  useEffect(() => {
    if (!frete?.id || !fontes.escutarUltimaPosicao) {
      setAoVivo(null);
      return undefined;
    }

    setAoVivo(null);
    const cancelar = fontes.escutarUltimaPosicao(
      frete.id,
      (snap) => {
        setAoVivo(
          posicaoDoCaminhao((snap?.docs || []).map((doc) => doc.data())),
        );
      },
      (e) => console.error("Erro ao acompanhar a posição do motorista:", e),
    );

    return () => cancelar && cancelar();
  }, [frete?.id, fontes]);

  useEffect(() => {
    const mapa = mapaRef.current;
    if (!mapa) return;

    if (trilhaRef.current) {
      mapa.removeLayer(trilhaRef.current);
      trilhaRef.current = null;
    }
    if (!freteId) return;

    const camada = L.layerGroup().addTo(mapa);
    trilhaRef.current = camada;

    // Uma polyline por trecho contínuo. Ligar tudo numa linha só desenharia por cima do buraco de
    // rastreio um caminho que ninguém registrou.
    const linha = [];
    for (const trecho of resumo?.trechos || []) {
      const pontosDoTrecho = trecho.filter(coordenadaValida);
      if (!pontosDoTrecho.length) continue;
      if (pontosDoTrecho.length > 1) {
        L.polyline(pontosDoTrecho, {
          color: AZUL_DO_TRAJETO,
          weight: 4,
          opacity: 0.75,
          smoothFactor: 2,
        }).addTo(camada);
      }
      linha.push(...pontosDoTrecho);
    }

    if (linha.length && linha.length <= TETO_DE_PONTOS_DESENHADOS) {
      for (const par of linha) {
        L.circleMarker(par, {
          radius: 2.5,
          color: CINZA_PERCURSO,
          weight: 0,
          fillColor: CINZA_PERCURSO,
          fillOpacity: 0.85,
        }).addTo(camada);
      }
    }

    for (const lacuna of resumo?.lacunas || []) {
      if (!coordenadaValida(lacuna.de) || !coordenadaValida(lacuna.ate))
        continue;
      L.polyline([lacuna.de, lacuna.ate], {
        color: COR_DA_LACUNA,
        weight: 3,
        opacity: 0.9,
        dashArray: "6 8",
      })
        .bindTooltip(
          `Sem rastreio por ${duracao(lacuna.minutos)} — de ${dataHora(lacuna.deMs)} a ${dataHora(lacuna.ateMs)}`,
        )
        .addTo(camada);
    }

    for (const parada of resumo?.paradas || []) {
      const par = [parada.latitude, parada.longitude];
      if (!coordenadaValida(par)) continue;
      L.circleMarker(par, {
        radius: compacto ? 6 : 8,
        color: "#ffffff",
        weight: 2,
        fillColor: CINZA_PERCURSO,
        fillOpacity: 1,
      })
        .bindTooltip(
          `Parado ${duracao(parada.minutos)} · ${parada.pontos} capturas · desde ${dataHora(parada.inicioMs)}`,
        )
        .addTo(camada);
    }

    for (const etapa of resumo?.operacionais || []) {
      const par = [etapa.latitude, etapa.longitude];
      if (!coordenadaValida(par)) continue;
      L.circleMarker(par, {
        radius: compacto ? 5 : 7,
        color: "#ffffff",
        weight: 2,
        fillColor: corDoStatus(etapa.freightStatus),
        fillOpacity: 1,
      })
        .bindTooltip(
          `${rotuloDoStatus(etapa.freightStatus)} — ${dataHora(etapa.ms)}`,
        )
        .addTo(camada);
    }

    const marcarPino = (lat, lng, cor, titulo) => {
      if (lat === null || lng === null) return;
      L.marker([lat, lng], { icon: pinoDeGota(cor, titulo) })
        .bindTooltip(titulo)
        .addTo(camada);
    };

    marcarPino(coletaLat, coletaLng, COR_DA_COLETA, "Coleta");
    marcarPino(entregaLat, entregaLng, COR_DA_ENTREGA, "Entrega");
  }, [freteId, resumo, compacto, coletaLat, coletaLng, entregaLat, entregaLng]);

  useEffect(() => {
    const mapa = mapaRef.current;
    if (!mapa) return;

    if (motoristaRef.current) {
      mapa.removeLayer(motoristaRef.current);
      motoristaRef.current = null;
    }
    if (!freteId || motoristaLat === null || motoristaLng === null) return;

    const titulo = `Motorista${motoristaMs ? ` — ${dataHora(motoristaMs)}` : ""}`;
    // Camada própria: posição nova não redesenha a viagem. E zIndexOffset: o caminhão passa por cima
    // de parada e etapa quando o motorista está em cima delas, que é o caso mais comum — parado na doca.
    motoristaRef.current = L.marker([motoristaLat, motoristaLng], {
      icon: pinoDoMotorista(titulo),
      zIndexOffset: 1000,
    })
      .bindTooltip(titulo)
      .addTo(mapa);
  }, [freteId, motoristaLat, motoristaLng, motoristaMs]);

  useEffect(() => {
    const mapa = mapaRef.current;
    if (!mapa) return;

    if (canhotosRef.current) {
      mapa.removeLayer(canhotosRef.current);
      canhotosRef.current = null;
    }
    marcadoresDosCanhotosRef.current = new Map();
    if (!freteId || !listaDeCanhotos?.length) return;

    const camada = L.layerGroup().addTo(mapa);
    canhotosRef.current = camada;

    for (const canhoto of listaDeCanhotos) {
      const par = [canhoto.latitude, canhoto.longitude];
      if (!coordenadaValida(par)) continue;

      // A margem de erro do GPS: o motorista estava em algum lugar dentro do círculo, não no pino.
      if (canhoto.precisao > 0) {
        L.circle(par, {
          radius: canhoto.precisao,
          color: VIOLETA_DO_CANHOTO,
          weight: 1,
          opacity: 0.5,
          fillOpacity: 0.1,
          interactive: false,
        }).addTo(camada);
      }

      const referencia = coordenadaValida(canhoto.referencia)
        ? canhoto.referencia
        : null;
      if (referencia) {
        L.polyline([par, referencia], {
          color: VIOLETA_DO_CANHOTO,
          weight: 2,
          opacity: 0.9,
          dashArray: "4 6",
          interactive: false,
        }).addTo(camada);
        L.circleMarker(referencia, {
          radius: 4,
          color: VIOLETA_DO_CANHOTO,
          weight: 2,
          fillColor: "#ffffff",
          fillOpacity: 1,
          interactive: false,
        }).addTo(camada);
      }

      // Acima de coleta e entrega, que o canhoto conferido cobre por ter sido anexado na porta. Abaixo
      // do caminhão, que só cede a vez quando o operador pede para ver o canhoto.
      const marcador = L.marker(par, {
        icon: pinoDoCanhoto(),
        zIndexOffset: 500,
      })
        .bindTooltip(dicaDoCanhoto(canhoto))
        .addTo(camada);
      marcadoresDosCanhotosRef.current.set(canhoto.id, { marcador, referencia });
    }
  }, [freteId, listaDeCanhotos]);

  // Só enquadra ao trocar de frete, com o que houver na primeira vez que houver algo. Reenquadrar a
  // cada posição nova arrancaria o mapa da mão de quem estivesse arrastando.
  useEffect(() => {
    const mapa = mapaRef.current;
    if (!mapa || !freteId || enquadradoRef.current === freteId) return;

    const enquadrar = [
      ...(resumo?.trechos || []).flat(),
      [coletaLat, coletaLng],
      [entregaLat, entregaLng],
      [motoristaLat, motoristaLng],
      ...pontosDosCanhotos(listaDeCanhotos),
    ].filter(coordenadaValida);
    if (!enquadrar.length) return;

    enquadradoRef.current = freteId;
    if (listaDeCanhotos) canhotosEnquadradosRef.current = freteId;
    if (enquadrar.length === 1) mapa.setView(enquadrar[0], 13);
    else mapa.fitBounds(enquadrar, { padding: [28, 28] });
  }, [
    freteId,
    resumo,
    coletaLat,
    coletaLng,
    entregaLat,
    entregaLng,
    motoristaLat,
    motoristaLng,
    listaDeCanhotos,
  ]);

  // Os pontos de parada chegam por escuta, quase sempre depois do primeiro enquadramento. Quando
  // chegam, a vista abre uma vez para caber o canhoto que ficou de fora, e só abre: canhoto que chega
  // com a tela aberta não tira o mapa da mão de ninguém.
  useEffect(() => {
    const mapa = mapaRef.current;
    if (!mapa || !freteId || !listaDeCanhotos) return;
    if (enquadradoRef.current !== freteId) return;
    if (canhotosEnquadradosRef.current === freteId) return;

    canhotosEnquadradosRef.current = freteId;
    const vista = mapa.getBounds();
    const fora = pontosDosCanhotos(listaDeCanhotos).filter(
      (par) => !vista.contains(par),
    );
    if (fora.length) {
      mapa.fitBounds(vista.extend(L.latLngBounds(fora)), { padding: [28, 28] });
    }
  }, [freteId, listaDeCanhotos]);

  // Só o pedido do operador leva o mapa até o canhoto. A lista fica fora das dependências de propósito:
  // cada escrita num ponto de parada puxaria a vista de volta.
  useEffect(() => {
    const mapa = mapaRef.current;
    const alvo = marcadoresDosCanhotosRef.current.get(focoId);
    if (!mapa || !alvo) return;

    const par = alvo.marcador.getLatLng();
    if (alvo.referencia) {
      mapa.fitBounds([par, alvo.referencia], {
        padding: [48, 48],
        maxZoom: ZOOM_DO_CANHOTO,
      });
    } else {
      mapa.setView(par, ZOOM_DO_CANHOTO);
    }

    for (const { marcador } of marcadoresDosCanhotosRef.current.values()) {
      marcador.closeTooltip().setZIndexOffset(500);
    }
    // Num frete encerrado a última posição do motorista costuma estar em cima do último canhoto, e o
    // caminhão esconderia justo o pino que o operador pediu para ver.
    alvo.marcador.setZIndexOffset(1500).openTooltip();
  }, [focoId, focoVez]);

  const pontos = (resumo?.trechos || []).reduce(
    (soma, trecho) => soma + trecho.length,
    0,
  );

  return (
    <div className={compacto ? "mapa-rota mapa-rota--compacto" : "mapa-rota"}>
      <div ref={containerRef} className="mapa-rota__tela" />

      {!compacto && (
        <div className="mapa-rota__legenda">
          <span>
            <i style={{ background: COR_DA_COLETA }} /> Coleta
          </span>
          <span>
            <i style={{ background: COR_DA_ENTREGA }} /> Entrega
          </span>
          <span>
            <i className="mapa-rota__legenda-caminhao">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M2 6.5h11.2v8.2H2z" />
                <path d="M13.2 9.4h3.6l3.2 3.1v2.2h-6.8z" />
                <circle cx="6.6" cy="17" r="2.1" />
                <circle cx="16.9" cy="17" r="2.1" />
              </svg>
            </i>{" "}
            Motorista
          </span>
          {listaDeCanhotos?.length > 0 && (
            <span>
              <i
                className="mapa-rota__legenda-canhoto"
                style={{ background: VIOLETA_DO_CANHOTO }}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d={GLIFO_DO_CANHOTO} />
                </svg>
              </i>{" "}
              Canhoto
            </span>
          )}
          <span>
            <i style={{ background: AZUL_DO_TRAJETO }} /> Trajeto percorrido
          </span>
          <span>
            <i style={{ background: CINZA_PERCURSO }} /> Parada e deslocamento
          </span>
          <span>
            <i className="mapa-rota__tracejado" /> Sem rastreio
          </span>
          <span>
            <i style={{ background: corDoStatus("WAITING_LOADING") }} />{" "}
            Aguardando
          </span>
          <span>
            <i style={{ background: corDoStatus("IN_TRANSIT") }} /> Em trânsito
          </span>
          <span>
            <i style={{ background: corDoStatus("UNLOADING_COMPLETED") }} />{" "}
            Etapa concluída
          </span>
        </div>
      )}

      <p className="mapa-rota__nota">
        {!frete ? (
          "Selecione um frete na lista."
        ) : erro ? (
          <span className="mapa-rota__nota--erro">{erro}</span>
        ) : carregando && !pontos ? (
          "Carregando o trajeto..."
        ) : !pontos ? (
          "Este frete ainda não tem posição registrada."
        ) : (
          <>
            {pontos} {pontos === 1 ? "ponto" : "pontos"} no trajeto
            {resumo?.paradas?.length
              ? ` · ${resumo.paradas.length} parada(s)`
              : ""}
            {resumo?.lacunas?.length
              ? ` · ${resumo.lacunas.length} intervalo(s) sem rastreio`
              : ""}
            {resumo?.legendaDisponivel === false
              ? " · rastreio no formato antigo, sem etapa operacional para colorir"
              : ""}
            {resumo?.faltou ? ` · trecho mais ${resumo.faltou} não carregado` : ""}
          </>
        )}
      </p>
    </div>
  );
}

export default MapaRota;
